import { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import AccountModal from '../components/AccountModal';
import { getAllAccounts, deleteAccount, getTotalBalanceByCurrency } from '../services/accountService';
import type { BankAccount } from '../types/account';
import { Plus, Building2, Pencil, Trash2, CreditCard } from 'lucide-react';

const BankAccountCard = ({ acc, onEdit, onDelete }: { acc: BankAccount, onEdit: (a: BankAccount) => void, onDelete: (id: string) => void }) => {
  const [expanded, setExpanded] = useState(false);
  const color = acc.color || '#3b82f6';
  
  return (
    <div 
      className="relative rounded-2xl p-5 overflow-hidden shadow-lg transition-transform hover:-translate-y-1 fade-in-up"
      style={{
        background: `linear-gradient(135deg, ${color}dd 0%, ${color}88 100%)`,
        border: `1px solid ${color}40`,
        color: '#fff'
      }}
    >
      <div className="absolute top-0 right-0 p-4 opacity-10 blur-xl pointer-events-none">
        <CreditCard className="w-32 h-32" />
      </div>

      <div className="relative z-10 flex justify-between items-start mb-6">
        <div>
          <p className="text-sm font-medium opacity-90">{acc.bankName}</p>
          <p className="text-xs uppercase tracking-wider opacity-70 mt-0.5">{acc.accountName} {acc.accountType}</p>
        </div>
        {acc.accountNumberLast4 && (
          <p className="text-sm tracking-widest opacity-80 font-mono">
            •••• {acc.accountNumberLast4}
          </p>
        )}
      </div>

      <div className="relative z-10">
        <p className="text-3xl font-bold tracking-tight">
          {acc.currency} {acc.balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </p>
      </div>

      <div className="relative z-10 mt-5 pt-3 border-t border-white/20 flex justify-between items-center text-xs opacity-80">
        <span className="capitalize">{acc.accountType} Account</span>
        <div className="flex gap-2">
           <button onClick={() => onEdit(acc)} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors" title="Edit">
              <Pencil className="w-3.5 h-3.5" />
           </button>
           <button onClick={() => setExpanded(!expanded)} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors text-red-100 hover:text-red-500" title="Delete Actions">
              <Trash2 className="w-3.5 h-3.5" />
           </button>
        </div>
      </div>

      {expanded && (
        <div className="relative z-10 mt-3 p-3 bg-red-500/90 rounded-xl text-center fade-in-up">
           <p className="text-xs font-bold mb-2 text-white">Delete this account?</p>
           <div className="flex gap-2">
             <button onClick={() => setExpanded(false)} className="flex-1 py-1.5 rounded bg-white/20 hover:bg-white/30 text-xs font-medium transition-colors">Cancel</button>
             <button onClick={() => onDelete(acc.id)} className="flex-1 py-1.5 rounded bg-white font-bold text-red-600 hover:bg-gray-100 text-xs transition-colors shadow">Delete</button>
           </div>
        </div>
      )}
    </div>
  );
};

const BankAccountsPage = () => {
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAcc, setEditingAcc] = useState<BankAccount | null>(null);

  const refresh = useCallback(() => {
    setAccounts(getAllAccounts());
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleDelete = (id: string) => {
    deleteAccount(id);
    refresh();
  };

  const totals = getTotalBalanceByCurrency();
  const currencies = Object.keys(totals).sort();

  return (
    <div className="min-h-screen flex" style={{ background: '#0f0f13' }}>
      <Sidebar />
      <main className="flex-1 min-w-0 overflow-y-auto">
        <div className="p-6 max-w-6xl mx-auto">
          
          {/* Header */}
          <div className="flex items-start justify-between mb-8 fade-in-up">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-xl bg-cyan-500/15 flex items-center justify-center">
                  <Building2 className="h-4 w-4 text-cyan-400" />
                </div>
                <h1 className="text-2xl font-bold text-white">Bank Accounts</h1>
              </div>
              <p className="text-sm text-gray-500">Manage your checking, savings, and cash balances</p>
            </div>
            <button
              onClick={() => { setEditingAcc(null); setModalOpen(true); }}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-cyan-600 hover:bg-cyan-500 transition-colors shadow-lg shadow-cyan-500/25 flex-shrink-0"
            >
              <Plus className="h-4 w-4" /> Add Account
            </button>
          </div>

          {/* Aggregated Totals row */}
          {currencies.length > 0 && (
            <div className="flex flex-wrap gap-4 mb-8 fade-in-up delay-100">
              {currencies.map(cur => (
                <div key={cur} className="glass-card rounded-2xl p-5 min-w-[200px] flex-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Total {cur} Balance</p>
                  <p className="text-2xl font-bold text-white">{cur} {totals[cur].toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                </div>
              ))}
            </div>
          )}

          {/* Accounts Grid */}
          {accounts.length === 0 ? (
            <div className="glass-card rounded-2xl p-12 text-center fade-in-up delay-200">
              <div className="w-16 h-16 rounded-2xl bg-cyan-500/10 flex items-center justify-center mx-auto mb-4">
                <Building2 className="h-7 w-7 text-cyan-400" />
              </div>
              <p className="text-white font-medium mb-1">No accounts added yet</p>
              <p className="text-sm text-gray-500">Click "Add Account" to track your balances.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 fade-in-up delay-200">
              {accounts.map(acc => (
                <BankAccountCard 
                  key={acc.id} 
                  acc={acc} 
                  onEdit={(a) => { setEditingAcc(a); setModalOpen(true); }} 
                  onDelete={handleDelete} 
                />
              ))}
            </div>
          )}

        </div>
      </main>

      <AccountModal 
        isOpen={modalOpen} 
        onClose={() => setModalOpen(false)} 
        onSaved={refresh} 
        editingAccount={editingAcc} 
      />
    </div>
  );
};

export default BankAccountsPage;
